/**
 * Reading a document the person just uploaded, and offering back a DRAFT they confirm.
 *
 * Three documents, three shapes, one flow: a resume becomes a candidate draft, a bill becomes an
 * expense claim draft, an Aadhaar or PAN card or a cancelled cheque becomes an identity draft for
 * an onboarding form. Every one of them is a PROPOSAL — these routes create nothing. The client
 * renders the draft into the form the person was already filling, they correct it, and the
 * EXISTING create route (`createCandidate`, `createClaim`, the onboarding profile update) is what
 * writes. That is invariant 9's rule for AI writes applied to extraction generally: the machine
 * proposes, the person commits.
 *
 * TWO PIPELINES, AND THE SPLIT IS THE POINT.
 *
 *   resume, receipt  ->  OCR -> DLP mask -> model -> Zod schema
 *   identity         ->  OCR -> regex + checksum, NEVER a model
 *
 * Aadhaar, PAN and bank account are encrypted at rest under invariant 7. Sending them to a
 * third-party inference API to have them read back would mean the plaintext has already left the
 * building before it is ever encrypted, and the DLP scanner that masks exactly these values on
 * their way to the model would have to be switched off to make it work. So identity extraction is
 * deterministic: Aadhaar carries a Verhoeff check digit, PAN has a fixed five-four-one structure
 * with a meaningful holder-type character, IFSC has a mandatory zero in position five. A regex
 * plus a checksum either matches or does not; a model returns a confident answer either way.
 *
 * SYNCHRONOUS, AND PAGE-CAPPED. A PDF with a real text layer is read in-process in milliseconds.
 * A scan or a phone photo needs OCR, which is 1-2 seconds for the documents this exists for — a
 * bill, an ID card, a one-page resume scan — and that is well inside a normal request. Rather than
 * a queue, a poll endpoint and a worker round trip to save a second on the common case, the route
 * REFUSES the documents that would actually hurt: past `MAX_SYNC_OCR_PAGES` it answers 413 and
 * says so. A 15-page scanned bank statement is not a form pre-fill, and a person uploading one has
 * asked the wrong question. Bulk import is where the queue earns its keep; it does not exist yet.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  instantSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** PDF, JPEG and PNG — what `storage.ts` already accepts, and what a phone camera produces. */
export const parseableMimeTypeSchema = z.enum(['application/pdf', 'image/jpeg', 'image/png']);

/**
 * Where the text came from. The client MUST show this: a value read from a PDF's own text layer
 * is exact, and a value read by OCR has a real per-character error rate. A form that pre-fills
 * both identically invites someone to tab past a misread digit in a bank account number.
 */
export const extractionSourceSchema = z.enum(['text-layer', 'ocr']);

/**
 * The upload body. Base64 rather than multipart, matching `uploadExpenseAttachmentBodySchema` —
 * every route in this area is JSON end to end, ts-rest types a JSON body natively, and the ~33%
 * base64 overhead on a phone photo costs less than a second request format and a multipart parser.
 * `app.ts` scopes a larger body limit to these paths, as it already does for expense attachments.
 */
export const parseDocumentBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  /** Enforced server-side against the real allow-list — a client can claim any type. */
  mimeType: parseableMimeTypeSchema,
  /** The file itself, base64-encoded, no data: URI prefix. */
  contentBase64: z.string().min(1),
});
export type ParseDocumentBody = z.infer<typeof parseDocumentBodySchema>;

/**
 * Pages OCR'd on the request path. Three covers a bill, an ID card front and back, and a short
 * scanned resume; past it the wait stops being a form pre-fill and starts being a batch job.
 */
export const MAX_SYNC_OCR_PAGES = 3;

/** `unreadable` is a normal outcome, not an error: a blurred photo has no text in it, and the
 *  person needs to be told that rather than shown a failure. */
export const parseStatusSchema = z.enum(['ready', 'unreadable']);

// --- resume -----------------------------------------------------------------

/**
 * THE SCHEMA IS THE PII BOUNDARY. This names identity and job facts only: no compensation, no
 * bank details, no PAN, Aadhaar, passport or date of birth. A resume in India routinely states
 * several of those, and the model cannot hand back a field that does not exist here — Zod strips
 * unknown keys on the way out. Widening what a resume yields means adding a field to this object,
 * which is a reviewable diff, not a prompt change nobody sees.
 *
 * THIS IS THE SOURCE. `candidateDraftSchema` in packages/ai is derived from it —
 * `resumeDraftSchema.required({firstName, lastName})` — rather than maintained as a second copy.
 * The copilot tool insists on a name because it offers its result as "create this candidate"; a
 * route pre-filling a form takes whatever the document gave. That one difference is the only one,
 * and it is expressed in code rather than trusted to two lists staying in step.
 */
export const resumeDraftSchema = z.object({
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: z.string().trim().min(1).max(100).optional(),
  email: z.string().trim().email().max(254).optional(),
  phone: z.string().trim().min(5).max(30).optional(),
  designation: z.string().trim().max(120).optional(),
  qualification: z.string().trim().max(200).optional(),
  experienceYears: z.number().min(0).max(60).optional(),
  city: z.string().trim().max(100).optional(),
  skills: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
  summary: z.string().trim().max(500).optional(),
});
export type ResumeDraft = z.infer<typeof resumeDraftSchema>;

// --- receipt ----------------------------------------------------------------

/**
 * A bill or invoice, in the shape `createExpenseClaim` already takes. MONEY IS A DECIMAL STRING,
 * never a number (invariant 2) — a JSON number for 12500.55 is a float the moment it is parsed,
 * and a float in a reimbursement total is a defect. `gstAmount` without `merchantGstin` is
 * refused for the same reason the create body refuses it: GST is only claimable against one.
 */
export const receiptDraftSchema = z
  .object({
    merchantName: z.string().trim().min(1).max(200).optional(),
    merchantGstin: z.string().trim().length(15).optional(),
    amount: decimalStringSchema.optional(),
    currency: z.string().trim().length(3).optional(),
    gstAmount: decimalStringSchema.optional(),
    spentOn: dateOnlySchema.optional(),
    /** The bill or invoice number printed on the document, for the claim's reference field. */
    reference: z.string().trim().max(100).optional(),
    description: z.string().trim().max(500).optional(),
  })
  .refine((draft) => draft.gstAmount === undefined || draft.merchantGstin !== undefined, {
    message:
      'merchantGstin is required when gstAmount is given — GST is only claimable against one',
    path: ['merchantGstin'],
  });
export type ReceiptDraft = z.infer<typeof receiptDraftSchema>;

// --- identity ---------------------------------------------------------------

export const identifierKindSchema = z.enum([
  'aadhaar',
  'pan',
  'ifsc',
  'bank_account',
  'uan',
  'esic',
]);
export type IdentifierKind = z.infer<typeof identifierKindSchema>;

/**
 * ONE IDENTIFIER, MASKED. `masked` shows the LAST FOUR CHARACTERS ONLY, and the full value never
 * appears in any response on this contract — it goes straight from the parser into
 * `encryptWithDek` on the server, and the person confirms it by recognising the last four digits
 * of the card in their hand. A response carrying a full Aadhaar would put it in a browser's
 * memory, in any proxy log along the way, and in whatever the support team screenshots.
 *
 * `confidence: 'repaired'` means OCR misread a glyph and exactly one reading satisfied the
 * checksum. It is almost certainly right — but "almost certainly" is the reason the client must
 * mark it, so a person looks twice before accepting it onto a statutory record.
 */
export const identifierDraftSchema = z.object({
  kind: identifierKindSchema,
  /** Masked: `XXXXXXXX9012`. Never the full value — see above. */
  masked: z.string(),
  confidence: z.enum(['exact', 'repaired']),
  /** 1-based page the value was found on, so the UI can point at it. */
  page: z.number().int().positive(),
  /** Surrounding text, already masked, so a person can see the context it was read from. */
  snippet: z.string(),
  /** Opaque, single-use, short-lived. The client hands this back to the route that commits the
   *  value, which is what lets the plaintext stay server-side for the whole round trip. */
  token: z.string(),
  tokenExpiresAt: instantSchema,
});
export type IdentifierDraft = z.infer<typeof identifierDraftSchema>;

export const identityDraftSchema = z.object({
  identifiers: z.array(identifierDraftSchema),
  /** Name and date of birth read off the card — not identifiers, so not masked or tokenised. */
  fullName: z.string().trim().max(200).optional(),
  dateOfBirth: dateOnlySchema.optional(),
});
export type IdentityDraft = z.infer<typeof identityDraftSchema>;

// --- result envelope --------------------------------------------------------

const parseResultBase = {
  status: parseStatusSchema,
  source: extractionSourceSchema.nullable(),
  /**
   * Things the person should know before trusting the draft: text was truncated, DLP masked a
   * span before the model saw it, OCR produced low-confidence output, a field was left blank
   * because the document did not state it. Rendered above the form, not swallowed.
   */
  warnings: z.array(z.string()),
};

export const resumeParseResultSchema = z.object({
  ...parseResultBase,
  draft: resumeDraftSchema.nullable(),
});

export const receiptParseResultSchema = z.object({
  ...parseResultBase,
  draft: receiptDraftSchema.nullable(),
});

export const identityParseResultSchema = z.object({
  ...parseResultBase,
  draft: identityDraftSchema.nullable(),
});

// --- committing what was read ----------------------------------------------

/**
 * Turn tokens into stored, encrypted identifiers.
 *
 * THE TOKEN IS THE PAYLOAD. The client sends back the opaque token it was given, never a value —
 * so the full Aadhaar makes exactly one journey, parser -> Redis -> `encryptWithDek`, and is
 * never in a request body, a browser's memory, a proxy log or a support screenshot. The server
 * redeems each token with GETDEL (one use, ever) and seals the result straight into the person's
 * own ciphertext column and blind index.
 *
 * FILLS AN EMPTY FIELD, NEVER REPLACES ONE. Onboarding is the case this exists for: a new hire
 * has no PAN on file and supplies it from their own card. CHANGING an identifier already on
 * record is a different act with different stakes, and it keeps its existing path through
 * `createProfileUpdateRequest` and HR approval. The route refuses an overwrite rather than
 * quietly preferring one value over the other.
 */
export const commitIdentifierSchema = z.object({
  kind: identifierKindSchema,
  /** The opaque, single-use token from a parse. Never a value. */
  token: z.string().min(1).max(128),
});

export const commitIdentityBodySchema = z.object({
  identifiers: z.array(commitIdentifierSchema).min(1).max(6),
});
export type CommitIdentityBody = z.infer<typeof commitIdentityBodySchema>;

export const commitIdentityResultSchema = z.object({
  /** Kinds actually written, in the order given. */
  stored: z.array(identifierKindSchema),
  /**
   * Kinds NOT written, each with a reason the UI shows verbatim: a token that had already been
   * used or expired, or a field that already holds a value and needs the approval path instead.
   */
  skipped: z.array(
    z.object({
      kind: identifierKindSchema,
      reason: z.enum(['token_expired', 'already_set', 'unsupported']),
    }),
  ),
});

// --- routes -----------------------------------------------------------------

export const documentParseContract = c.router(
  {
    /**
     * Resume -> candidate draft. `recruitment:manage` covers a recruiter adding a candidate to a
     * posting; `onboarding:manage` covers HR onboarding a hire from the same document. Both reach
     * the same parser — the difference is only which form the draft lands in.
     */
    parseResume: {
      method: 'POST',
      path: '/documents/parse/resume',
      body: parseDocumentBodySchema,
      responses: {
        200: resumeParseResultSchema,
        400: errorBodySchema,
        413: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'Read a resume and propose a candidate draft',
      // ai-hidden for a reason that is NOT the usual one. There is no PII risk in the response;
      // the problem is the request. A derived tool would hand the model a `contentBase64` field
      // and ask it to produce a whole file as base64, which it cannot do — it would hallucinate
      // bytes or spend its entire output budget trying. The copilot reads documents through the
      // attachment it was given (`extract_resume`, by id), which is the shape that works.
      metadata: routeMeta(['candidate:manage', 'onboarding:manage'], { ai: 'hidden' }),
    },

    /**
     * Bill or invoice -> expense claim draft. `expense:submit`, because the person parsing a
     * receipt is the person about to claim it.
     */
    parseReceipt: {
      method: 'POST',
      path: '/documents/parse/receipt',
      body: parseDocumentBodySchema,
      responses: {
        200: receiptParseResultSchema,
        400: errorBodySchema,
        413: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'Read a bill or invoice and propose an expense claim draft',
      // ai-hidden for the same reason as `parseResume` above: a model cannot supply a file. The
      // copilot's route to a bill is `extract_receipt`, which takes an attachment id.
      metadata: routeMeta('expense:submit', { ai: 'hidden' }),
    },

    /**
     * Identity document -> masked identity draft, for a new hire filling their own onboarding
     * form. `null` is signed-in-only on purpose: a new hire completing onboarding holds no HR
     * permission, and the route reads only the document in the request — it never touches another
     * person's record, so there is nothing to scope a permission against.
     *
     * `ai: 'hidden'` keeps it out of the copilot's tool list. The copilot may navigate someone to
     * the onboarding screen; it must never be the thing that pulls identifiers out of a document.
     */
    parseIdentityDocument: {
      method: 'POST',
      path: '/documents/parse/identity',
      body: parseDocumentBodySchema,
      responses: {
        200: identityParseResultSchema,
        400: errorBodySchema,
        413: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'Read an identity document and propose masked identifiers',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },

    /**
     * Store identifiers the caller just read off their OWN document. Signed-in-only and strictly
     * self-service: the route writes to `actor.personId` and takes no person id, so there is no
     * parameter through which one person could write another's Aadhaar. `ai: 'hidden'` for the
     * same reason as the parse route — the copilot must never be the thing that files a
     * government identifier, and it could not supply a token anyway.
     */
    commitIdentityDocument: {
      method: 'POST',
      path: '/documents/parse/identity/commit',
      body: commitIdentityBodySchema,
      responses: {
        200: commitIdentityResultSchema,
        400: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Store identifiers read from your own identity document',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },
  },
  { pathPrefix: '' },
);
